import { create } from 'zustand';
import {
  filterByLangValue,
  filterByName,
  sortByCost,
  sortByLangValue,
  sortByName,
} from '../features/table/helpers';
import { getContentFieldValue } from '../pages/manager/body/leads/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import {
  deleteLead,
  fetchLead,
  fetchLeads,
  FetchLeadsRequest,
  LeadType,
  postLead,
  restoreLead,
} from './api/leads-api';
import { useAlertStore } from './use-alert-store';

const initLead: Partial<LeadType> = {
  title: { ru: '' },
  content: [
    { type: 'full_name', is_required: true },
    { type: 'phone', is_required: true },
    { type: 'email', is_required: false },
  ],
  is_available: true,
};
interface Store extends Partial<PaginationType> {
  fetchLeads: (data: FetchLeadsRequest) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  restoreLead: (id: string) => Promise<void>;
  fetchLead: () => Promise<void>;
  postLead: () => Promise<void>;
  isLoadingFetch?: boolean;
  isLoading?: boolean;
  list?: LeadType[];
  lead?: Partial<LeadType>;
  handleUpdateLead: (data: Partial<LeadType>) => void;
  handleResetLead: () => void;
}

export const useLeadStore = create<Store>((set, get) => ({
  fetchLeads: async (params) => {
    set({ isLoadingFetch: true });
    try {
      const { leads, ...other } = await fetchLeads(params);
      let list = [...leads];

      if (params.name) {
        list = list.filter((item) => {
          const name = getContentFieldValue(item, 'full_name');
          return filterByName(name, params.name);
        });
      }

      if (params.phone) {
        list = list.filter((item) => {
          const phone = getContentFieldValue(item, 'phone');
          return filterByName(phone, params.phone);
        });
      }

      if (params.email) {
        list = list.filter((item) => {
          const email = getContentFieldValue(item, 'email');
          return filterByName(email, params.email);
        });
      }

      if (params.sex) {
        list = list.filter((item) => {
          const sex = getContentFieldValue(item, 'sex');
          return filterByName(sex, params.sex);
        });
      }

      if (params.age) {
        const age = params.age.split(',');
        list = list.filter((item) => {
          const itemAge = getContentFieldValue(item, 'age');
          if (!itemAge) return false;
          return age.includes(itemAge);
        });
      }

      if (params.rating) {
        list = list.filter((item) => item.rating.feedbacks == Number(params.rating));
      }

      if (params.source) {
        list = list.filter((item) => filterByLangValue(item.source, params.source));
      }

      if (params.field == 'id') {
        list = list.sort((a, b) => sortByCost(a.id, b.id, params.dir as SortType));
      }

      if (params.field == 'name') {
        list = list.sort((a, b) => {
          const aName = getContentFieldValue(a, 'full_name');
          const bName = getContentFieldValue(b, 'full_name');
          return sortByName(aName, bName, params.dir as SortType);
        });
      }

      if (params.field == 'phone') {
        list = list.sort((a, b) => {
          const aPhone = getContentFieldValue(a, 'phone');
          const bPhone = getContentFieldValue(b, 'phone');

          return sortByName(aPhone, bPhone, params.dir as SortType);
        });
      }

      if (params.field == 'email') {
        list = list.sort((a, b) => {
          const aEmail = getContentFieldValue(a, 'email');
          const bEmail = getContentFieldValue(b, 'email');

          return sortByName(aEmail, bEmail, params.dir as SortType);
        });
      }
      if (params.field == 'sex') {
        list = list.sort((a, b) => {
          const aSex = getContentFieldValue(a, 'sex');
          const bSex = getContentFieldValue(b, 'sex');

          return sortByName(aSex, bSex, params.dir as SortType);
        });
      }
      if (params.field == 'age') {
        list = list.sort((a, b) => {
          const aAge = getContentFieldValue(a, 'age');
          const bAge = getContentFieldValue(b, 'age');

          return sortByName(aAge, bAge, params.dir as SortType);
        });
      }

      if (params.field == 'rating') {
        list = list.sort((a, b) =>
          sortByCost(a.rating.feedbacks, b.rating.feedbacks, params.dir as SortType),
        );
      }

      if (params.field == 'source') {
        list = list.sort((a, b) => sortByLangValue(a.source, b.source, params.dir as SortType));
      }

      if (params.field == 'created_at') {
        list = list.sort((a, b) => sortByName(a.created_at, b.created_at, params.dir as SortType));
      }

      set({ list, ...other });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по лидам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  deleteLead: async (id) => {
    set({ isLoading: true });
    try {
      await deleteLead(id);
      useAlertStore.getState().add({ label: 'Лид успешно удален', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить лида', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreLead: async (id) => {
    set({ isLoading: true });
    try {
      await restoreLead(id);
      useAlertStore.getState().add({ label: 'Лид успешно восстановлен', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось восстановить лида', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchLead: async () => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchLead();
      set({ lead: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по форме лидозахвата', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  postLead: async () => {
    set({ isLoading: true });
    try {
      const lead = get().lead;
      if (!lead) {
        throw new Error('Lead is not defined');
      }
      await postLead(lead);
      useAlertStore
        .getState()
        .add({ label: 'Форма лидозахвата успешно изменена', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось добавить данные по лиду', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  handleUpdateLead: async (data) => {
    set((store) => ({ ...store, lead: { ...store.lead, ...data } }));
  },
  handleResetLead: async () => {
    set({ lead: initLead });
  },
  lead: initLead,
}));
